/**
 * Disposition PERSONNALISÉE des pages « Mes contacts » (migration 0251,
 * table `contact_layouts`) : chaque utilisateur active/masque et réordonne les
 * listes de contacts propres à son rôle, et retrouve sa disposition sur tous ses
 * appareils.
 *
 * - Le CATALOGUE des listes valides par page vit ICI (source de vérité) ; la base
 *   ne stocke que les choix de l'utilisateur. Sans ligne enregistrée : toutes les
 *   listes visibles, dans l'ordre par défaut (aucun changement pour l'existant).
 * - Fonctions pures (`resolveContactLayout`, `toggleSection`, `moveSection`) :
 *   toute la logique d'ordre est testée sans React ni réseau.
 * - Table postérieure aux types générés (`types.generated.ts` n'est jamais
 *   régénéré, cf. CLAUDE.md) → client générique.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

const db = supabase as unknown as SupabaseClient;

export type ContactPage = 'tutor' | 'student';

export interface ContactSectionDef {
  id: string;
  label: string;
}

/**
 * Listes configurables par page — l'ordre du tableau est l'ordre PAR DÉFAUT.
 * Les blocs d'action (« Demandes reçues », « Invitations envoyées », « Demandes
 * en attente ») ne sont volontairement pas configurables : ce sont des tâches à
 * traiter, pas des listes de contacts.
 */
export const CONTACT_SECTIONS = {
  tutor: [
    { id: 'classes', label: 'Mes classes' },
    { id: 'students', label: 'Mes élèves' },
    { id: 'alternants', label: 'Mes étudiants alternants' },
  ],
  student: [
    { id: 'tutors', label: 'Mes professeurs' },
    { id: 'parents', label: 'Mes parents' },
    { id: 'peers', label: 'Mes camarades' },
    { id: 'followed', label: 'Profs suivis' },
  ],
} as const satisfies Record<ContactPage, readonly ContactSectionDef[]>;

export type TutorSectionId = (typeof CONTACT_SECTIONS.tutor)[number]['id'];
export type StudentSectionId = (typeof CONTACT_SECTIONS.student)[number]['id'];

/** Choix enregistrés de l'utilisateur pour une page. */
export interface SavedLayout {
  /** Ordre choisi (ids), en premier = tout en haut. */
  order: string[];
  /** Listes désactivées (ids). */
  hidden: string[];
}

/** Liste avec son état effectif, dans l'ordre d'affichage. */
export interface ResolvedSection {
  id: string;
  label: string;
  enabled: boolean;
}

/**
 * Ordre et état effectifs des listes d'une page.
 *
 * Ordre = choix enregistré (ids inconnus et doublons ignorés), puis les listes
 * absentes du choix — par exemple ajoutées au catalogue APRÈS l'enregistrement —
 * à la suite, dans l'ordre par défaut. `defaultOrder` permet à une page d'adapter
 * son ordre par défaut à ses données (ex. élève sans professeur) : il n'a d'effet
 * que sur ce qui n'est pas déjà ordonné par l'utilisateur.
 */
export function resolveContactLayout(
  defs: readonly ContactSectionDef[],
  saved: SavedLayout | null,
  defaultOrder?: readonly string[],
): ResolvedSection[] {
  const byId = new Map(defs.map((d) => [d.id, d]));
  const baseOrder = [
    ...(defaultOrder ?? []).filter((id) => byId.has(id)),
    ...defs.map((d) => d.id),
  ];
  const savedOrder = (saved?.order ?? []).filter((id) => byId.has(id));
  const hidden = new Set(saved?.hidden ?? []);

  return [...new Set([...savedOrder, ...baseOrder])].map((id) => ({
    id,
    label: byId.get(id)!.label,
    enabled: !hidden.has(id),
  }));
}

/** Choix à enregistrer pour refléter exactement l'état effectif affiché. */
function toSaved(sections: readonly ResolvedSection[]): SavedLayout {
  return {
    order: sections.map((s) => s.id),
    hidden: sections.filter((s) => !s.enabled).map((s) => s.id),
  };
}

/**
 * Active ou masque une liste. ACTIVER une liste la fait passer TOUT EN HAUT
 * (l'utilisateur voit tout de suite l'effet) ; la masquer conserve sa position.
 * Sans effet si la liste est déjà dans l'état demandé.
 */
export function toggleSection(
  sections: readonly ResolvedSection[],
  id: string,
  enabled: boolean,
): SavedLayout {
  const target = sections.find((s) => s.id === id);
  if (!target || target.enabled === enabled) return toSaved(sections);

  if (enabled) {
    const reordered = [{ ...target, enabled: true }, ...sections.filter((s) => s.id !== id)];
    return toSaved(reordered);
  }
  return toSaved(sections.map((s) => (s.id === id ? { ...s, enabled: false } : s)));
}

/** Échange une liste avec sa voisine (dans la liste complète affichée, masquées comprises). Borné. */
export function moveSection(
  sections: readonly ResolvedSection[],
  id: string,
  direction: 'up' | 'down',
): SavedLayout {
  const index = sections.findIndex((s) => s.id === id);
  const swapWith = direction === 'up' ? index - 1 : index + 1;
  if (index === -1 || swapWith < 0 || swapWith >= sections.length) return toSaved(sections);

  const next = [...sections];
  [next[index], next[swapWith]] = [next[swapWith]!, next[index]!];
  return toSaved(next);
}

// ── Persistance ───────────────────────────────────────────────────────────────

const rowSchema = z.object({
  section_order: z.array(z.string()),
  hidden_sections: z.array(z.string()),
});

/**
 * Disposition de l'utilisateur courant pour une page. `defaultOrder` (optionnel)
 * adapte l'ordre par défaut aux données de la page tant que rien n'est enregistré.
 * Une erreur de lecture retombe sur la disposition par défaut : la page ne casse jamais.
 */
export function useContactLayout(page: ContactPage, defaultOrder?: readonly string[]) {
  const { user } = useAuth();
  const userId = user?.id;
  const qc = useQueryClient();
  const queryKey = ['contact-layout', userId, page] as const;

  const query = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: 5 * 60_000,
    retry: 1,
    queryFn: async (): Promise<SavedLayout | null> => {
      const { data, error } = await db
        .from('contact_layouts')
        .select('section_order, hidden_sections')
        .eq('user_id', userId!)
        .eq('page', page)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const parsed = rowSchema.safeParse(data);
      return parsed.success
        ? { order: parsed.data.section_order, hidden: parsed.data.hidden_sections }
        : null;
    },
  });

  const saved = query.data ?? null;
  const defaultOrderKey = defaultOrder?.join(',') ?? '';
  const sections = useMemo(
    () => resolveContactLayout(CONTACT_SECTIONS[page], saved, defaultOrder),
    // `defaultOrder` change d'identité à chaque rendu : on suit son contenu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, saved, defaultOrderKey],
  );

  // Écriture optimiste : l'ordre change tout de suite, restauré si le serveur refuse.
  const save = useMutation({
    mutationFn: async (next: SavedLayout) => {
      if (!userId) throw new Error('not_authenticated');
      const { error } = await db.from('contact_layouts').upsert(
        {
          user_id: userId,
          page,
          section_order: next.order,
          hidden_sections: next.hidden,
        },
        { onConflict: 'user_id,page' },
      );
      if (error) throw new Error(error.message);
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<SavedLayout | null>(queryKey);
      qc.setQueryData<SavedLayout | null>(queryKey, next);
      return { previous };
    },
    onError: (_err, _next, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData<SavedLayout | null>(queryKey, context.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });

  const reset = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('not_authenticated');
      const { error } = await db
        .from('contact_layouts')
        .delete()
        .eq('user_id', userId)
        .eq('page', page);
      if (error) throw new Error(error.message);
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<SavedLayout | null>(queryKey);
      qc.setQueryData<SavedLayout | null>(queryKey, null);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData<SavedLayout | null>(queryKey, context.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });

  return {
    /** Toutes les listes de la page, dans l'ordre effectif (masquées comprises). */
    sections,
    /** Ids des listes à afficher, dans l'ordre. */
    visibleIds: sections.filter((s) => s.enabled).map((s) => s.id),
    /** Vrai tant que la préférence n'est pas connue (évite un saut d'ordre à l'affichage). */
    isLoading: query.isLoading,
    /** Une disposition est enregistrée (sinon : défaut). */
    isCustomized: saved !== null,
    isSaving: save.isPending || reset.isPending,
    toggle: (id: string, enabled: boolean) => save.mutate(toggleSection(sections, id, enabled)),
    move: (id: string, direction: 'up' | 'down') =>
      save.mutate(moveSection(sections, id, direction)),
    reset: () => reset.mutate(),
  };
}
